import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { FormKeys } from '@nx-customer-apps/shared/enums';
import { FormOptions, ModernizationAndInsultaionOptions, QuestionOverride } from '@nx-customer-apps/shared/interfaces';
import {
    CheckboxQuestion,
    MIN_BUILDING_CONSTRUCTION_YEAR,
    MultiQuestionbox,
    Question,
    TextboxQuestion
} from '@nx-customer-apps/shared/utils';
import { CommonFormService, ValidatorsService } from '../../../../services';

@Injectable({
    providedIn: 'root'
})
export class ModernizationsFormService {
    constructor(
        private commonFormService: CommonFormService,
        private validatorsService: ValidatorsService
    ) {}

    public getModernizationQuestions(options?: FormOptions): Question[] {
        return [this.commonFormService.getBuildingConstructionYear(options), this.getModernizations(options)];
    }

    public getBasementRenovationYear(override?: QuestionOverride) {
        return new TextboxQuestion({
            key: FormKeys.BasementRenovationYear,
            order: 9,
            label: 'COMMON.BUILDING.RENOVATION_YEAR',
            floatLabel: 'always',
            styles: 'ca-form-field-s ca-form-field-w-65 vp-form-field-center with-error-message',
            defaultErrorMessage: 'HEATLOAD.MODERNIZATIONS_AND_INSULATION.BASEMENT.YEAR.ERRORS.REQUIRED',
            validators: [
                Validators.required,
                this.validatorsService.minMaxValidator({ min: MIN_BUILDING_CONSTRUCTION_YEAR, max: new Date().getFullYear() })
            ],
            mask: { pattern: '0000' },
            value: override?.value,
            parentGroup: FormKeys.Renovations
        });
    }

    public getModernizations(options?: FormOptions | ModernizationAndInsultaionOptions): MultiQuestionbox {
        const questions = [];

        const hasRoofModernization = this.getHasRoofModernization(options?.questions?.[FormKeys.HasRoofRenovationYear]);
        questions.push(hasRoofModernization);

        const roofModernizationOverride = options?.questions?.[FormKeys.RoofRenovationYear];
        if (roofModernizationOverride) {
            const roofModernization = this.getRoofModernizationYear(roofModernizationOverride);
            questions.push(roofModernization);
        }

        const hasWallsModernization = this.getHasWallsModernization(options?.questions?.[FormKeys.HasWallsRenovationYear]);
        questions.push(hasWallsModernization);

        const wallsModernizationOverride = options?.questions?.[FormKeys.WallsRenovationYear];
        if (wallsModernizationOverride) {
            const wallsModernization = this.getWallsModernizationYear(wallsModernizationOverride);
            questions.push(wallsModernization);
        }

        const hasWindowsModernization = this.getHasWindowsModernization(options?.questions?.[FormKeys.HasWindowsConstructionYear]);
        questions.push(hasWindowsModernization);

        const windowsModernizationOverride = options?.questions?.[FormKeys.WindowsConstructionYear];
        if (windowsModernizationOverride) {
            const windowsModernization = this.getWindowsModernizationYear(windowsModernizationOverride);
            questions.push(windowsModernization);
        }

        const hasAtticRenovationOverride = options?.questions?.[FormKeys.HasAtticRenovationYear];
        if (hasAtticRenovationOverride) {
            const hasAtticRenovation = this.getHasAtticRenovation(hasAtticRenovationOverride);
            questions.push(hasAtticRenovation);
        }

        const atticRenovationOverride = options?.questions?.[FormKeys.AtticRenovationYear];
        if (atticRenovationOverride) {
            const atticRenovationYear = this.getAtticRenovationYear(atticRenovationOverride);
            questions.push(atticRenovationYear);
        }

        if (!(options as ModernizationAndInsultaionOptions)?.basementPresent) {
            return new MultiQuestionbox({
                question: 'HEATLOAD.MODERNIZATIONS.RENOVATIONS.TITLE',
                description: 'HEATLOAD.MODERNIZATIONS.RENOVATIONS.DESCRIPRION',
                groupName: FormKeys.Renovations,
                styles: 'flex flex-col',
                questions
            });
        }

        const hasBasementRenovationOverride = options?.questions?.[FormKeys.HasBasementRenovationYear];
        const hasBasementRenovation = this.getHasBasementRenovation(hasBasementRenovationOverride);
        questions.push(hasBasementRenovation);

        const basementRenovationYearOverride = options?.questions?.[FormKeys.BasementRenovationYear];
        if (basementRenovationYearOverride) {
            const basementRenovation = this.getBasementRenovationYear(options?.questions?.[FormKeys.BasementRenovationYear]);
            questions.push(basementRenovation);
        }

        return new MultiQuestionbox({
            question: 'HEATLOAD.MODERNIZATIONS.RENOVATIONS.TITLE',
            description: 'HEATLOAD.MODERNIZATIONS.RENOVATIONS.DESCRIPRION',
            groupName: FormKeys.Renovations,
            styles: 'flex flex-col',
            questions
        });
    }

    private getHasRoofModernization(override?: QuestionOverride) {
        return new CheckboxQuestion({
            order: 0,
            key: FormKeys.HasRoofRenovationYear,
            label: 'COMMON.BUILDING.ROOF',
            value: override?.value
        });
    }

    private getHasWallsModernization(override?: QuestionOverride) {
        return new CheckboxQuestion({
            order: 2,
            key: FormKeys.HasWallsRenovationYear,
            label: 'COMMON.BUILDING.WALL',
            value: override?.value
        });
    }

    private getHasWindowsModernization(override?: QuestionOverride) {
        return new CheckboxQuestion({
            order: 4,
            key: FormKeys.HasWindowsConstructionYear,
            label: 'COMMON.BUILDING.WINDOWS',
            value: override?.value
        });
    }

    private getHasAtticRenovation(override?: QuestionOverride) {
        return new CheckboxQuestion({
            order: 6,
            key: FormKeys.HasAtticRenovationYear,
            label: 'COMMON.BUILDING.UPPER_FLOOR',
            value: override?.value
        });
    }

    private getHasBasementRenovation(override?: QuestionOverride) {
        return new CheckboxQuestion({
            order: 8,
            key: FormKeys.HasBasementRenovationYear,
            label: 'COMMON.BUILDING.BASEMENT',
            value: override?.value
        });
    }

    public getRoofModernizationYear(override?: QuestionOverride) {
        return new TextboxQuestion({
            key: FormKeys.RoofRenovationYear,
            order: 1,
            label: 'COMMON.BUILDING.RENOVATION_YEAR',
            floatLabel: 'always',
            styles: 'ca-form-field-s ca-form-field-w-65 vp-form-field-center with-wrapped-error-message-450',
            defaultErrorMessage: 'HEATLOAD.MODERNIZATIONS_AND_INSULATION.ROOF.YEAR.ERRORS.REQUIRED',
            validators: [
                Validators.required,
                this.validatorsService.minMaxValidator({ min: MIN_BUILDING_CONSTRUCTION_YEAR, max: new Date().getFullYear() })
            ],
            mask: { pattern: '0000' },
            value: override?.value,
            parentGroup: FormKeys.Renovations
        });
    }

    public getWallsModernizationYear(override?: QuestionOverride) {
        return new TextboxQuestion({
            order: 3,
            key: FormKeys.WallsRenovationYear,
            label: 'COMMON.BUILDING.RENOVATION_YEAR',
            floatLabel: 'always',
            styles: 'ca-form-field-s ca-form-field-w-65 vp-form-field-center with-wrapped-error-message-450',
            defaultErrorMessage: 'HEATLOAD.MODERNIZATIONS_AND_INSULATION.WALLS.YEAR.ERRORS.REQUIRED',
            validators: [
                Validators.required,
                this.validatorsService.minMaxValidator({ min: MIN_BUILDING_CONSTRUCTION_YEAR, max: new Date().getFullYear() })
            ],
            mask: { pattern: '0000' },
            value: override?.value,
            parentGroup: FormKeys.Renovations
        });
    }

    public getWindowsModernizationYear(override?: QuestionOverride) {
        return new TextboxQuestion({
            order: 5,
            key: FormKeys.WindowsConstructionYear,
            label: 'COMMON.BUILDING.RENOVATION_YEAR',
            floatLabel: 'always',
            styles: 'ca-form-field-s ca-form-field-w-65 vp-form-field-center with-wrapped-error-message-450',
            defaultErrorMessage: 'HEATLOAD.MODERNIZATIONS.WINDOWS.ERRORS.REQUIRED',
            validators: [
                Validators.required,
                this.validatorsService.minMaxValidator({ min: MIN_BUILDING_CONSTRUCTION_YEAR, max: new Date().getFullYear() })
            ],
            mask: { pattern: '0000' },
            value: override?.value,
            parentGroup: FormKeys.Renovations
        });
    }

    public getAtticRenovationYear(override?: QuestionOverride) {
        return new TextboxQuestion({
            order: 7,
            key: FormKeys.AtticRenovationYear,
            label: 'COMMON.BUILDING.RENOVATION_YEAR',
            floatLabel: 'always',
            styles: 'ca-form-field-s ca-form-field-w-65 vp-form-field-center with-error-message',
            defaultErrorMessage: 'HEATLOAD.MODERNIZATIONS_AND_INSULATION.UPPER_FLOOR.YEAR.ERRORS.REQUIRED',
            validators: [
                Validators.required,
                this.validatorsService.minMaxValidator({ min: MIN_BUILDING_CONSTRUCTION_YEAR, max: new Date().getFullYear() })
            ],
            parentGroup: FormKeys.Renovations,
            mask: { pattern: '0000' },
            value: override?.value
        });
    }
}
